import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Plus, Edit, Trash2, FileText, Archive, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contracts = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    client_id: '',
    client_name: '',
    contract_number: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 365*24*60*60*1000).toISOString().split('T')[0],
    monthly_fee: 0,
    services: '',
    unit_price_pps: 15.0,
    pps_count: 1,
    installation_price: 0,
    drive_link: ''
  });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchContracts();
    fetchClients();
  }, []);

  const fetchContracts = async () => {
    try {
      const res = await axios.get(`${API}/contracts`);
      setContracts(res.data);
    } catch (error) {
      console.error('Failed to fetch contracts:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`);
      setClients(res.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/contracts/${editingId}`, formData);
        toast.success('Договорът бе актуализиран!');
      } else {
        await axios.post(`${API}/contracts`, formData);
        toast.success('Договорът бе добавен!');
      }
      fetchContracts();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Грешка при запазване');
    }
  };

  const handleDelete = async (id) => {
    if (user?.role !== 'admin') {
      toast.error('Само админи могат да изтриват договори');
      return;
    }
    if (window.confirm('Сигурни ли сте?')) {
      try {
        await axios.delete(`${API}/contracts/${id}`);
        toast.success('Договорът бе изтрит!');
        fetchContracts();
      } catch (error) {
        toast.error('Грешка при изтриване');
      }
    }
  };

  const handleArchive = async (id) => {
    if (user?.role !== 'admin') {
      toast.error('Само админи могат да архивират договори');
      return;
    }
    try {
      await axios.post(`${API}/contracts/${id}/archive`);
      toast.success('Договорът бе архивиран!');
      fetchContracts();
    } catch (error) {
      toast.error('Грешка при архивиране');
    }
  };

  const handleEdit = (contract) => {
    setFormData(contract);
    setEditingId(contract.id);
    setDialogOpen(true);
  };

  const resetForm = () => {
    const today = new Date();
    const contractNumber = `${today.getDate().toString().padStart(2, '0')}${(today.getMonth() + 1).toString().padStart(2, '0')}${today.getFullYear()}1`;
    
    setFormData({
      client_id: '',
      client_name: '',
      contract_number: contractNumber,
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date(Date.now() + 365*24*60*60*1000).toISOString().split('T')[0],
      monthly_fee: 0,
      services: '',
      unit_price_pps: 15.0,
      pps_count: 1,
      installation_price: 0,
      drive_link: ''
    });
    setEditingId(null);
  };

  const selectClient = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setFormData({
        ...formData,
        client_id: client.id,
        client_name: client.company_name,
        monthly_fee: client.monthly_fee,
        pps_count: client.vehicle_count || 1
      });
    }
  };

  const handleGenerateContract = async (contractId) => {
    try {
      const response = await axios.post(`${API}/contracts/${contractId}/generate-contract`, {}, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Договор_${contractId}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Договорът беизтеглен!');
    } catch (error) {
      toast.error('Грешка при генериране на договор');
    }
  };

  const handleGenerateProtocol = async (contractId) => {
    try {
      const response = await axios.post(`${API}/contracts/${contractId}/generate-protocol`, {}, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Протокол_${contractId}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Протоколът бе изтеглен!');
    } catch (error) {
      toast.error('Грешка при генериране на протокол');
    }
  };

  const getExpiryColor = (endDate) => {
    const today = new Date();
    const end = new Date(endDate);
    const diffDays = Math.floor((end - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) return 'expiry-red';
    if (diffDays < 90) return 'expiry-yellow';
    return 'expiry-green';
  };

  return (
    <div className="page-container" data-testid="contracts-page">
      <div className="page-header">
        <h1 className="page-title">{t('contracts')}</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-contract-btn">
              <Plus size={20} /> {t('add')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingId ? t('edit') : t('add')} {t('contracts')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="contract-form" data-testid="contract-form">
              <select
                value={formData.client_id}
                onChange={(e) => selectClient(e.target.value)}
                required
                className="form-select"
              >
                <option value="">Изберете клиент</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>{client.company_name}</option>
                ))}
              </select>
              <Input
                placeholder="Номер на договор (автоматично генериран)"
                value={formData.contract_number}
                onChange={(e) => setFormData({...formData, contract_number: e.target.value})}
                required
                disabled
              />
              <div className="form-field">
                <label className="field-label">Начална дата на договор *</label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  required
                />
              </div>
              <div className="form-field">
                <label className="field-label">Крайна дата на договор *</label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                  required
                />
              </div>
              <div className="form-field">
                <label className="field-label">Единична цена за 1 ППС (лв/месец) *</label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="напр. 15.00"
                  value={formData.unit_price_pps}
                  onChange={(e) => setFormData({...formData, unit_price_pps: parseFloat(e.target.value)})}
                  required
                />
              </div>
              <div className="form-field">
                <label className="field-label">Брой ППС (бройки GPS устройства) *</label>
                <Input
                  type="number"
                  placeholder="напр. 5"
                  value={formData.pps_count}
                  onChange={(e) => setFormData({...formData, pps_count: parseInt(e.target.value)})}
                  required
                />
              </div>
              <div className="form-field">
                <label className="field-label">Цена на инсталация (еднократна, лв)</label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="напр. 0 или 100"
                  value={formData.installation_price}
                  onChange={(e) => setFormData({...formData, installation_price: parseFloat(e.target.value)})}
                />
              </div>
              <div className="form-field col-span-2">
                <label className="field-label">Услуги включени в договора *</label>
                <Input
                  placeholder="GPS мониторинг, видеонаблюдение, 24/7 поддръжка"
                  value={formData.services}
                  onChange={(e) => setFormData({...formData, services: e.target.value})}
                  required
                />
              </div>
              <div className="form-field col-span-2">
                <label className="field-label">Google Drive линк (за съхранение на документи)</label>
                <Input
                  placeholder="https://drive.google.com/..."
                  value={formData.drive_link}
                  onChange={(e) => setFormData({...formData, drive_link: e.target.value})}
                />
              </div>
              <div className="form-actions">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  {t('cancel')}
                </Button>
                <Button type="submit" data-testid="save-contract-btn">{t('save')}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="contracts-grid">
        {contracts.filter(c => !c.is_archived).map(contract => (
          <div key={contract.id} className="contract-card" data-testid={`contract-card-${contract.id}`}>
            <div className="contract-card-header">
              <div>
                <h3>{contract.client_name}</h3>
                <p className="contract-number">№ {contract.contract_number}</p>
              </div>
              <span className={`expiry-badge ${getExpiryColor(contract.end_date)}`}>
                {new Date(contract.end_date).toLocaleDateString('bg-BG')}
              </span>
            </div>

            <div className="contract-card-body">
              <div className="contract-info-row">
                <span>{t('start_date')}:</span>
                <span>{new Date(contract.start_date).toLocaleDateString('bg-BG')}</span>
              </div>
              <div className="contract-info-row">
                <span>{t('monthly_fee')}:</span>
                <span className="fee-highlight">{contract.monthly_fee} лв</span>
              </div>
              <div className="contract-info-row">
                <span>{t('services')}:</span>
                <span>{contract.services}</span>
              </div>
            </div>

            <div className="contract-card-footer">
              <div className="contract-actions">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => handleGenerateContract(contract.id)}
                  data-testid={`generate-contract-${contract.id}`}
                >
                  <FileText size={16} /> Договор
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => handleGenerateProtocol(contract.id)}
                  data-testid={`generate-protocol-${contract.id}`}
                >
                  <Download size={16} /> Протокол
                </Button>
              </div>
              <div className="contract-admin-actions">
                <Button variant="ghost" size="icon" onClick={() => handleEdit(contract)} data-testid={`edit-contract-${contract.id}`}>
                  <Edit size={16} />
                </Button>
                {user?.role === 'admin' && (
                  <>
                    <Button variant="ghost" size="icon" onClick={() => handleArchive(contract.id)} data-testid={`archive-contract-${contract.id}`}>
                      <Archive size={16} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(contract.id)} data-testid={`delete-contract-${contract.id}`}>
                      <Trash2 size={16} />
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Contracts;
